import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import formatMinutes from '../../../utils/formatMinutes';
import timesheetStatusColors from '../../../utils/timesheetStatusColors';
import { translateTimesheetStatuses } from '../../../utils/timesheetStatusLabels';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';
import { applyAttendancePreset, stripAttendancePreset } from '../../../utils/attendanceFilterQuery';
import { useTaFilterColumns } from './taFilterColumns';

// "09:00:00" (TimeSpan) → "09:00".
export const formatClock = (v) => (typeof v === 'string' && v.includes(':') ? v.substring(0, 5) : (v || '-'));

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $bg }) => $bg || '#9e9e9e15'};
  color: ${({ $color }) => $color || '#9e9e9e'};
`;

// Devam Paneli (AKM POC §5.15) — preset: panelden gelen süzgeçler (useAttendancePreset). DataTable süzgeç listesini
// tohumlayamadığı (mui-datatables 4.3.0 sütun dışı filterList okumaz) ve onTableChanged parametreleri SIFIRDAN kurduğu
// için preset YAPIŞKANDIR: sayfa her tablo değişiminde parametreleri applyPreset'ten geçirir; yoksa 2. sayfada sessizce
// düşerdi (ObPeriodApprovalsPage ile aynı tuzak). Kullanıcının tablodaki tarih seçimi panel tarihini ezer.
// AKM POC §7.5/§7.6 (Raporlar planı) — ortak süzgeç seti tablonun kendi süzgeç sütunlarıdır (useTaFilterColumns: statü,
// grup, departman, lokasyon, vardiya, kişi — görünmez sütunlar); planlanan vardiya saatleri, eksik süre, hesaplanan ve
// onaylanan fazla mesai sütunları her raporda. "Durum" süzgeci kapalı (gün raporu uçları desteklemez).
// Eksik Süre artık her raporda (eski includeUndertime seçeneği gereksiz — verilse de yok sayılır).
export function useTaReportApi(fetchFn, { preset = null } = {}) {
  const { t, i18n } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [queryParams, setQueryParams] = useState(() => applyAttendancePreset({ pageSize: 10, pageNumber: 1 }, preset));
  const [filterList, setFilterList] = useState([]);
  const lastValidQueryParams = useRef(null);

  // Preset değişince (ör. "Süzgeci Kaldır") yalnız panelin koyduğu parametreler değiştirilir.
  const presetRef = useRef(preset);
  const presetKey = preset ? JSON.stringify(preset) : '';
  useEffect(() => {
    const previous = presetRef.current;
    presetRef.current = preset;
    if ((previous ? JSON.stringify(previous) : '') === presetKey) return;
    setQueryParams((prev) => applyAttendancePreset(stripAttendancePreset(prev, previous), preset));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetKey]);

  const applyPreset = useCallback((params) => applyAttendancePreset(params, presetRef.current), []);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    fetchFn(paramsToUse)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .catch(() => {
        setData([]);
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger, fetchFn]);

  const refresh = useCallback(() => {
    setRefreshTrigger((p) => p + 1);
  }, []);

  // Tek durum haritası (utils/timesheetStatusLabels) — partialLeave dahil hiçbir durum ham görünmez.
  const statusLabels = useMemo(() => translateTimesheetStatuses(t), [t]);
  const filterColumns = useTaFilterColumns(t);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'userFullName', label: t('timesheet.employee'),
      options: {
        filter: true, sort: true,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <PersonCell name={v} profilePicture={row?.userProfilePicture} />;
        },
      },
    },
    dateFilterColumn('date', t('timesheet.date'), i18n.language),
    {
      name: 'workHourName', label: t('timesheet.workHour'),
      options: { filter: false, sort: false },
    },
    {
      name: 'plannedStart', label: t('taReports.columns.plannedStart'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatClock(v) },
    },
    {
      name: 'plannedEnd', label: t('taReports.columns.plannedEnd'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatClock(v) },
    },
    {
      name: 'expectedMinutes', label: t('timesheet.expected'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'actualMinutes', label: t('timesheet.actual'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'lateMinutes', label: t('timesheet.late'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'earlyLeaveMinutes', label: t('pdks.earlyLeave'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'undertimeMinutes', label: t('taReports.undertime.column'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'outsideMinutes', label: t('timesheet.outside'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'totalComputedOvertimeMinutes', label: t('taReports.columns.computedOvertime'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'approvedOvertimeMinutes', label: t('taReports.columns.approvedOvertime'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'status', label: t('timesheet.status'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => {
          const colors = timesheetStatusColors[v] || { bg: '#9e9e9e15', text: '#9e9e9e' };
          return <StatusBadge $bg={colors.bg} $color={colors.text}>{statusLabels[v] || v}</StatusBadge>;
        },
      },
    },
    ...filterColumns,
  ], [t, i18n.language, data, statusLabels, filterColumns]);

  return {
    columns, data, fetching, pagination, refresh, queryParams, setQueryParams, filterList, setFilterList, applyPreset,
    statusLabels,
  };
}
